/**
 * The one rule both workflow save paths apply before replacing `workflows/{id}` with
 * `merge: false` (SYD-3057): a save is stale unless the `meta.lastModified` the saver loaded
 * equals the one stored right now, read inside the same transaction that writes. Consumers:
 * the editor's update transaction in `react/src/workflows/editor` and the `updateWorkflow`
 * callable in `functions/main`.
 *
 * Equality, not ordering: a stored stamp the saver never saw means the saver's copy is not
 * the document, whichever way the clocks fall. Absent compares as absent, so a document
 * that never carried a stamp still saves when the saver loaded it that way, and a saver
 * that sends no stamp against a stamped document is refused rather than trusted.
 *
 * Either side may be a client or admin `Timestamp` (both expose `seconds`/`nanoseconds`
 * through prototype getters), its `{ _seconds, _nanoseconds }` callable-response form, its
 * `{ seconds, nanoseconds }` JSON form, or the ISO string a lean tool projection returns.
 * Every shape reduces to whole milliseconds first, because an ISO string carries no finer
 * grain than that. A `FieldValue` sentinel or anything unreadable counts as absent.
 */

const stampMillis = (stamp: unknown): number | null => {
  if (typeof stamp === 'string') {
    const parsed = Date.parse(stamp);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof stamp !== 'object' || stamp === null) return null;
  const { seconds, nanoseconds, _seconds, _nanoseconds } = stamp as Record<string, unknown>;
  if (typeof seconds === 'number' && typeof nanoseconds === 'number') {
    return seconds * 1000 + Math.floor(nanoseconds / 1_000_000);
  }
  if (typeof _seconds === 'number' && typeof _nanoseconds === 'number') {
    return _seconds * 1000 + Math.floor(_nanoseconds / 1_000_000);
  }
  return null;
};

export const workflowSaveIsStale = (storedLastModified: unknown, loadedLastModified: unknown): boolean =>
  stampMillis(storedLastModified) !== stampMillis(loadedLastModified);
